import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AssignPeopleDialogComponent,
  AssignPeopleDialogComponentData,
} from '@shared/component/job/dialog/assign-people-dialog/assign-people-dialog.component';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  firstValueFrom,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';


import { User } from '@modules/user/models/user.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JobService } from '@modules/job/services/job.service';
import { Job, OrderDetail } from '@modules/job/models/job.model';
import { ACTIVE_USER } from '@modules/auth/providers/auth.provider';
import {
  AppEventEnum,
  JobActionEnum,
  JobStatusEnum,
  OrderStatusEnum,
  PaymentStatusEnum,
  PermissionEnum,
  RoleEnum,
} from '@generated/graphql';
import { JobApi } from '@modules/job/api/job.api';
import { CommonService } from '@shared/services/common.service';
import { ExtraJob } from '@modules/extra-job/models/extra-job.model';
import { ExtraJobApi } from '@modules/extra-job/api/extra-job.api';
import { ExtraJobsUpdateOrCreateEvent } from '@modules/job/components/job-detail/extra-jobs/extra-jobs.component';
import { COMMON_SERVICE_CONFIG } from '@shared/providers/common-service.provider';
import { BusinessPlanService } from '@app/modules/account-settings/pages/business-profile/services/business-plan.service';
import { PaymentService } from '@shared/component/payments/services/payment.service';
import { APP_ROUTES, AppRoutes } from '@config/app-routes.config';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentProviderAccountService } from '@modules/payment/services/payment-provider-account.service';
import { Payment } from '@modules/payment/models/payment.model';
import moment from 'moment';
import {
  EditEstimatedDateComponent
} from '@modules/job/components/dialog/edit-estimated-date/edit-estimated-date.component';
import { CommonHelper } from '@shared/helpers/common.helper';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { Duration } from 'luxon';
import { ApprovalEnum, SnackBarTypeEnum } from '@shared/types/commonEnum';
import { ExpertService } from '@modules/user/services/expert.service';
import { MatTabChangeEvent } from "@angular/material/tabs";
import {
  CompanyProfileTab,
  COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES
} from "@modules/company/pages/company-profile-page/company-profile-page.component";
import {CheckoutDialogComponent} from '@shared/component/payments/dialogs/checkout-dialog/checkout-dialog.component';
import { ScreenSpinnerService } from "@shared/component/full-screen-spinner/screen-spinner.service";
import {
  PaymentApprovalDialogComponent
} from "@shared/component/payments/dialogs/payment-approval-dialog/payment-approval-dialog.component";
import { PaymentOfJobApi } from "@modules/payment/api/payment-of-job.api";
import { JobFeesDialogComponent } from '../../components/dialog/job-fees-dialog/job-fees-dialog.component';
import {
  ConfirmDialogAction,
  ConfirmDialogComponent,
  ConfirmDialogComponentData,
} from '@shared/component/dialog/confirm-dialog/confirm-dialog.component';
import { shareReplay } from "rxjs/operators";
import { EventSubscription, PusherService } from '@shared/services/pusher.service';

export enum JobDetailTab {
  Conversation = 'conversation',
  MainRequest = 'main-request',
  ScopeAndFee = 'scope-and-fee',
  JobFiles = 'job-files',
  DocumentTemplates = 'document-templates',
  ExtraJobs = 'extra-jobs',
}

export const JOB_DETAIL_MAKE_PAYMENT_ACTION = 'make-payment';
export const JOB_DETAIL_ACTION_QUERY_PARAM = 'action';
export const JOB_DETAIL_EXTRA_JOB_ID_QUERY_PARAM = 'extraJobId';
export const JOB_DETAIL_TAB_QUERY_PARAM = 'tab';

export const buildExtraJobsTabQueryParams = (
  extraJobId?: string | null,
  extraQueryParams: Record<string, string | null | undefined> = {},
): Record<string, string> => {
  const queryParams: Record<string, string> = {
    [JOB_DETAIL_TAB_QUERY_PARAM]: JobDetailTab.ExtraJobs,
  };

  if (extraJobId) {
    queryParams[JOB_DETAIL_EXTRA_JOB_ID_QUERY_PARAM] = extraJobId;
  }

  Object.entries(extraQueryParams).forEach(([key, value]) => {
    if (value) {
      queryParams[key] = value;
    }
  });

  return queryParams;
};

export const buildScopeAndFeeTabQueryParams = (
  extraQueryParams: Record<string, string | null | undefined> = {},
): Record<string, string> => {
  const queryParams: Record<string, string> = {
    [JOB_DETAIL_TAB_QUERY_PARAM]: JobDetailTab.ScopeAndFee,
  };

  Object.entries(extraQueryParams).forEach(([key, value]) => {
    if (value) {
      queryParams[key] = value;
    }
  });

  return queryParams;
};

export const buildJobMakePaymentQueryParams = (): Record<string, string> =>
  buildScopeAndFeeTabQueryParams({
    [JOB_DETAIL_ACTION_QUERY_PARAM]: JOB_DETAIL_MAKE_PAYMENT_ACTION,
  });

export const buildExtraJobMakePaymentQueryParams = (extraJobId: string): Record<string, string> =>
  buildExtraJobsTabQueryParams(extraJobId, {
    [JOB_DETAIL_ACTION_QUERY_PARAM]: JOB_DETAIL_MAKE_PAYMENT_ACTION,
  });


@UntilDestroy()
@Component({
  selector: 'app-job-detail',
  templateUrl: './job-detail.component.html',
  styleUrls: ['./job-detail.component.scss'],
  providers: [COMMON_SERVICE_CONFIG],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailPageComponent implements OnInit, OnDestroy {
  @ViewChild('confirmBillingDialogTemplate') confirmBillingDialogTemplate
  @ViewChild('confirmProfileDialogTemplate') confirmProfileDialogTemplate
  @ViewChild('confirmDocumentsPreparedDialogTemplate') confirmDocumentsPreparedDialogTemplate
  @ViewChild('confirmMarkAsCompleteDialogTemplate') confirmMarkAsCompleteDialogTemplate // New ViewChild
  readonly JobActionEnum = JobActionEnum
  readonly job$: Observable<Job> = this.jobService.activeEntity$.pipe(
    shareReplay(1)
  )

  readonly customers$: Observable<User[]>

  readonly experts$: Observable<User[]>

  readonly extraJobs$: Observable<ExtraJob[]>

  private readonly tabSelectionSync$ = new BehaviorSubject<void>(undefined);
  private readonly extraJobsState$ = new BehaviorSubject<ExtraJob[]>([]);
  private extraJobsLoadedForJobId: string | null = null;

  public expertsCanAssigned$: Observable<User[]>;

  readonly payment$ = this.job$.pipe(
    map(job => {
      if (job.parentId) {
        return job?.parent?.order?.payment
      }

      return job?.order?.payment
    })
  )

  public canAssignExpert$: Observable<boolean> = this.activeUser$.pipe(
    map(user => user && user.hasPermissions(PermissionEnum.AssignExpertToJob))
  );

  public canAddCustomers$: Observable<boolean> = this.activeUser$.pipe(
    map(user => user && user.hasPermissions(PermissionEnum.AllowCustomerToParticipateInJob))
  );

  public visibleExtraJobs$ = this.businessPlanService.isPremiumPlan$;

  public estimatedTime: number;

  protected readonly PaymentStatusEnum = PaymentStatusEnum;

  readonly visiblePayment$ = this.businessPlanService.visiblePayment$()

  readonly isMakePaymentDisabled$: Observable<boolean>;

  readonly showMarkDone$: Observable<boolean>

  readonly visibleTabs$: Observable<JobDetailTab[]> = combineLatest([
    this.job$,
    this.activeUser$,
    this.visibleExtraJobs$,
  ]).pipe(
    map(([job, activeUser, visibleExtraJobs]) => {
      const tabs: JobDetailTab[] = [
        JobDetailTab.Conversation,
      ];

      if (!job?.parentId) {
        tabs.push(JobDetailTab.MainRequest);
      }

      tabs.push(
        JobDetailTab.ScopeAndFee,
        JobDetailTab.JobFiles,
      );

      if (job?.status !== JobStatusEnum.Draft && Boolean(visibleExtraJobs)) {
        tabs.push(JobDetailTab.ExtraJobs);
      }

      if (activeUser?.isCorporateExpert()) {
        tabs.push(JobDetailTab.DocumentTemplates);
      }

      return tabs;
    }),
    distinctUntilChanged((prev, next) => prev.join('|') === next.join('|')),
    shareReplay(1)
  );

  readonly selectedIndex$: Observable<number> = combineLatest([
    this.route.queryParamMap,
    this.visibleTabs$,
    this.tabSelectionSync$,
  ]).pipe(
    map(([queryParams, visibleTabs]) => {
      const tabName = this.resolveTab(
        this.getCurrentBrowserQueryParam(JOB_DETAIL_TAB_QUERY_PARAM)
        ?? queryParams.get(JOB_DETAIL_TAB_QUERY_PARAM),
      );
      const index = tabName ? visibleTabs.indexOf(tabName) : -1;

      return index >= 0 ? index : 0;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  )

  readonly focusedExtraJobId$: Observable<string | null> = this.route.queryParamMap.pipe(
    map((queryParams) => queryParams.get(JOB_DETAIL_EXTRA_JOB_ID_QUERY_PARAM)),
    distinctUntilChanged(),
    shareReplay(1),
  );

  readonly conversationTabActive$ = this.createTabActive$(JobDetailTab.Conversation);
  readonly mainRequestTabActive$ = this.createTabActive$(JobDetailTab.MainRequest);
  readonly scopeAndFeeTabActive$ = this.createTabActive$(JobDetailTab.ScopeAndFee);
  readonly jobFilesTabActive$ = this.createTabActive$(JobDetailTab.JobFiles);
  readonly extraJobsTabActive$ = this.createTabActive$(JobDetailTab.ExtraJobs);
  readonly documentTemplatesTabActive$ = this.createTabActive$(JobDetailTab.DocumentTemplates);

  jobId: string;

  jobStatus = JobStatusEnum;

  protected readonly JobStatusEnum = JobStatusEnum;

  readonly CompanyProfileTab = CompanyProfileTab;
  readonly CompanyProfileTabQueryParam = COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES;

  private paymentStatusSubscription: EventSubscription | null = null;
  private extraJobPaymentSubscriptions = new Map<string, EventSubscription>();
  private primaryPaymentId: string | null = null;
  public confirmBillingLoading = false;

  constructor(
    @Inject(APP_ROUTES) public readonly appRoutes: AppRoutes,
    @Inject(ACTIVE_USER) protected readonly activeUser$: Observable<User>,
    protected readonly jobService: JobService,
    protected readonly jobApi: JobApi,
    protected readonly expertService: ExpertService,
    protected readonly commonService: CommonService,
    protected readonly cdr: ChangeDetectorRef,
    private readonly extraJobApi: ExtraJobApi,
    private readonly businessPlanService: BusinessPlanService,
    private readonly paymentService: PaymentService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly paymentProviderAccountService: PaymentProviderAccountService,
    private readonly router: Router,
    private readonly screenSpinnerService: ScreenSpinnerService,
    public commonHelper: CommonHelper,
    private readonly _snackBarService: SnackBarService,
    private readonly paymentOfJobApi: PaymentOfJobApi,
    private readonly pusherService: PusherService
  ) {
    this.experts$ = this.job$.pipe(
      map(job => job?.experts ?? [])
    )

    this.customers$ = this.job$.pipe(
      map(job => job?.customers ?? [])
    )

    this.extraJobs$ = this.extraJobsState$.asObservable();

    this.showMarkDone$ = this.job$.pipe(
      map((job) => job?.status === JobStatusEnum.Ongoing),
      distinctUntilChanged()
    );

    this.isMakePaymentDisabled$ = combineLatest([
      this.job$,
      this.paymentService.processingPaymentIds$,
      this.paymentService.processingOrderIds$,
      this.paymentService.awaitingRefreshOrderIds$,
    ]).pipe(
      map(([job, processingPaymentIds, processingOrderIds, awaitingRefreshOrderIds]) => {
        const primaryOrder = this.getPrimaryPaymentCheckoutJob(job)?.order ?? null;
        const payment = primaryOrder?.payment ?? null;

        if (!primaryOrder || !payment) {
          return true;
        }

        if (processingOrderIds.has(primaryOrder.id)) {
          return true;
        }

        if (awaitingRefreshOrderIds.has(primaryOrder.id)) {
          return true;
        }

        if (payment.id && processingPaymentIds.has(payment.id)) {
          return true;
        }

        if (primaryOrder.status === OrderStatusEnum.Paid) {
          return true;
        }

        if (
          job?.status === JobStatusEnum.Closed ||
          job?.status === JobStatusEnum.Draft
        ) {
          return true;
        }

        return !payment.canMakePayment;
      }),
    )


    this.expertsCanAssigned$ = this.job$.pipe(
      filter(job => !!job),
      switchMap((job) =>
        job ? this.expertService.getByCountryOfResponsibility$(
          job.supportedCountry.country.id
        ) : of([])
      ),
      untilDestroyed(this)
    )
  }

  ngOnInit(): void {
    this.pusherService.setup();

    this.route.params.pipe(
      map(params => params.id as string | undefined),
      filter((id): id is string => Boolean(id)),
      distinctUntilChanged(),
      tap(() => this.resetExtraJobsState()),
      switchMap(id => this.jobApi.find$(id)),
      filter((entity): entity is Job => Boolean(entity)),
      tap(entity => this.jobService.dispatchSetActiveEntityAction(entity)),
      untilDestroyed(this)
    ).subscribe();

    this.handleMakePaymentRouteAction();
    this.handleExtraJobsTabDataLoading();
    this.subscribeToPaymentUpdates();
  }

  ngOnDestroy(): void {
    this.paymentStatusSubscription?.leaveChannel();
    this.extraJobPaymentSubscriptions.forEach(subscription => subscription.leaveChannel());
    this.extraJobPaymentSubscriptions.clear();
  }

  public onOpenExpertDialog(job: Job): void {
    const dialog = this.openExtraJobDialog(
      'Assign Expert',
      job,
      RoleEnum.Expert,
    );

    dialog.componentInstance.assignUsers
      .pipe(
        combineLatestWith(this.job$.pipe(first())),
        switchMap(([value, job]) => {
          this.commonService.snackBarService.dismiss();

          return this.syncQuery$(
            dialog,
            this.jobApi.syncExpertsToJob$({
              id: job.id,
              userIds: value.map((item) => item.id),
            }),
          );
        }),
      ).subscribe();
  }

  public onOpenCustomerDialog(job: Job): void {
    const dialog = this.openExtraJobDialog(
      'Add Customer',
      job,
      RoleEnum.Customer,
    );

    dialog.componentInstance.assignUsers.pipe(
      combineLatestWith(this.job$.pipe(first())),
      switchMap(([value, job]) => {
        this.commonService.snackBarService.dismiss();

        return this.syncQuery$(
          dialog,
          this.jobApi.syncCustomersToJob$({
            id: job.id,
            userIds: value.map((item) => item.id),
          }),
          'Add Success!',
        );
      }),
    ).subscribe();
  }

  protected syncQuery$(
    dialog: MatDialogRef<AssignPeopleDialogComponent, User[]>,
    query$: Observable<Job>,
    successText: string = 'Assign Success!',
  ) {
    return query$.pipe(
      tap(job => {
        if (job) {
          this.jobService.dispatchSetActiveEntityAction(job);
        }

        dialog.close();

        this.commonService.snackBarService.pushSuccessAlert(successText);

        this.cdr.markForCheck();
      }),
      catchError((e) => {
        this.commonService.snackBarService.pushErrorMessage();

        return EMPTY;
      }),
    );
  }

  public onDeleteExtraJob(item: ExtraJob): void {
    this.extraJobApi
      .delete$(item.id)
      .pipe(
        switchMap((result) => {
          if (!result) {
            this.commonService.snackBarService.pushErrorMessage();
            return EMPTY;
          }

          this.commonService.snackBarService.pushAlert(
            'Extra job has been deleted',
          );

          return this.refreshExtraJobs$(item.jobId);
        }),
        catchError((e) => {
          console.error(e);

          item.loading = false;

          return EMPTY;
        }),
      )
      .subscribe();
  }

  onCloseExtraJob(item: ExtraJob) {
    this.extraJobApi
      .close$(item.id)
      .pipe(
        switchMap((result) => {
          if (!result) {
            this.commonService.snackBarService.pushErrorMessage();
            return EMPTY;
          }

          this.commonService.snackBarService.pushAlert(
            'The extra job is now closed.',
          );

          return this.refreshExtraJobs$(item.jobId);
        }),
        catchError((e) => {
          console.error(e);

          item.loading = false;

          return EMPTY;
        }),
      )
      .subscribe();
  }

  onConfirmExtraJob(item: ExtraJob) {
    this.extraJobApi
      .confirm$(item.id)
      .pipe(
        switchMap((result) => {
          if (!result) {
            this.commonService.snackBarService.pushErrorMessage();
            return EMPTY;
          }

          this.commonService.snackBarService.pushAlert(
            'Extra job has been completed',
            'Well done',
            SnackBarTypeEnum.success,
          );

          return this.refreshExtraJobs$(item.jobId);
        }),
        catchError((e) => {
          console.error(e);
          item.loading = false;

          return EMPTY;
        }),
      )
      .subscribe();
  }

  public onUpdateOrCreateExtraJob($event: ExtraJobsUpdateOrCreateEvent): void {
    const [componentInstance, input] = $event;
    const { commonService } = componentInstance;
    const { snackBarService, errorService } = commonService;

    snackBarService.dismiss();

    commonService.setLoading(true);

    this.extraJobApi
      .updateOrCreate$(input)
      .pipe(
        switchMap((result) => {
          commonService.setLoading(false);

          if (!result) {
            snackBarService.pushErrorMessage();
            componentInstance.onCloseDialog();

            return EMPTY;
          }

          if (input.id) {
            snackBarService.pushUpdateSuccessMessage();
          } else {
            snackBarService.pushAlert(
              'Extra job created successfully!',
              null,
              SnackBarTypeEnum.success,
            );
          }

          componentInstance.onCloseDialog();

          return this.refreshExtraJobs$(result.jobId);
        }),
        catchError(({ graphQLErrors }) => {
          commonService.setLoading(false);

          errorService
            .getValidationErrorObjectList(graphQLErrors)
            .setScrollEl(componentInstance.scrollEl)
            .showErrors(componentInstance.form);

          return EMPTY;
        }),
      )
      .subscribe();
  }

  public openPaymentReceipt(payment: Payment): void {
    this.paymentService.openPaymentReceipt(payment)
  }

  public openProofOfPayment(payment: Payment): void {
    this.paymentService.openProofOfPayment(payment)
  }

  protected openExtraJobDialog(
    title: string,
    job: Job,
    role: RoleEnum,
  ): MatDialogRef<AssignPeopleDialogComponent, User[]> {
    return this.dialog.open<
      AssignPeopleDialogComponent,
      AssignPeopleDialogComponentData,
      User[]
    >(AssignPeopleDialogComponent, {
      width: AssignPeopleDialogComponent.width,
      autoFocus: false,
      data: {
        title,
        job,
        role,
      },
    });
  }

  public getEstimatedTime(job: Job): number {
    if (job.estimatedTime) {
      return (this.estimatedTime = Duration.fromISO(job.estimatedTime).as(
        'days',
      ));
    }

    const { service, detailObject } = job;
    const matchedMainService = detailObject?.jobServiceOrder?.mainServices?.find((item) => {
      const mainServiceId = item?.service?.service?.id;

      if (!mainServiceId) {
        return false;
      }

      return service?.id ? mainServiceId === service.id : true;
    })?.service;

    return (this.estimatedTime = matchedMainService?.getEstimatedTime?.() ?? 0);
  }

  public getJobDeadline(job: Job) {
    const { createdAt } = job;
    return moment(createdAt).add(this.estimatedTime, 'days').toDate();
  }

  public onEditEstimatedDate(job: Job) {
    const dialog = this.dialog.open(EditEstimatedDateComponent, {
      autoFocus: false,
      width: '40vw',
      data: {
        job,
        estimatedTime: this.estimatedTime,
      },
    });

    dialog.afterClosed().pipe(
      switchMap((result) => {
        if (!result) {
          return EMPTY;
        }

        this._snackBarService.pushUpdateSuccessMessage();

        return this._reloadCurrentJob(job.id);
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  public canCloseJob(job: Job): boolean {
    return (
      job.status !== JobStatusEnum.Closed &&
      job.status !== JobStatusEnum.Draft &&
      job.status !== JobStatusEnum.Completed &&
      job.status !== JobStatusEnum.Done
    );
  }

  cancelJobConfirmed(job: Job): void {
    this.jobService.cancelJob(job.id).pipe(
      switchMap((result) => result ? this._reloadCurrentJob(job.id) : EMPTY),
      untilDestroyed(this),
    ).subscribe();
  }

  markJobDone(job: Job): void {
    this.jobService.updateJob(job.id, {
      status: JobStatusEnum.Done,
    }).pipe(
      switchMap((result) => result ? this._reloadCurrentJob(job.id) : EMPTY),
      untilDestroyed(this),
    ).subscribe();
  }

  async onTabChange(event: MatTabChangeEvent): Promise<void> {
    const selectedTabIndex = event.index;
    const visibleTabs = await firstValueFrom(this.visibleTabs$);
    const tabName = visibleTabs[selectedTabIndex];
    const currentTab = this.resolveTab(
      this.getCurrentBrowserQueryParam(JOB_DETAIL_TAB_QUERY_PARAM)
      ?? this.route.snapshot.queryParamMap.get(JOB_DETAIL_TAB_QUERY_PARAM),
    );

    if (!tabName) {
      return;
    }

    if (currentTab === tabName) {
      return;
    }

    this.syncTabQueryParam(tabName);
  }

  onExtraJobsChange(extraJobs: ExtraJob[]): void {
    this.setExtraJobsState(extraJobs);
  }

  private handleExtraJobsTabDataLoading(): void {
    combineLatest([
      this.route.paramMap.pipe(
        map((params) => params.get('id') ?? ''),
        distinctUntilChanged(),
      ),
      this.selectedIndex$,
      this.visibleTabs$,
    ]).pipe(
      map(([jobId, selectedIndex, visibleTabs]) => ({
        jobId,
        activeTab: visibleTabs[selectedIndex] ?? null,
      })),
      distinctUntilChanged((prev, next) =>
        prev.jobId === next.jobId && prev.activeTab === next.activeTab,
      ),
      filter(({jobId, activeTab}) => Boolean(jobId) && activeTab === JobDetailTab.ExtraJobs),
      switchMap(({jobId}) => this.refreshExtraJobs$(jobId).pipe(
        catchError((e) => {
          console.error(e);
          return of(this.extraJobsState$.value);
        }),
      )),
      untilDestroyed(this),
    ).subscribe();
  }

  async confirmKyc(companyId: string) {
    await this.router.navigate(this.appRoutes.company(companyId), {
      queryParams: {
        tab: COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES[CompanyProfileTab.Kyc]
      }
    })
  }

  confirmBilling(): void {
    const confirmBillingDialogId = 'confirm-billing-dialog';
    const jobFeesDialogId = 'job-fees-dialog';
    if (this.dialog.getDialogById(confirmBillingDialogId) || this.dialog.getDialogById(jobFeesDialogId)) {
      this.setConfirmBillingLoading(false);
      return;
    }
    this.setConfirmBillingLoading(true);
    this.job$.pipe(
      first(),
      switchMap((job) => {
        if (!job || !job.services) {
          this._snackBarService.pushErrorMessage();
          this.setConfirmBillingLoading(false);
          return EMPTY;
        }

        // Check if any service fee is zero
        const hasZeroPriceItem = job.services.some(
          (service) => (service.service?.serviceFee || 0) <= 0
        );

        if (hasZeroPriceItem) {
          return this.openJobFeesDialogForBilling$(job, jobFeesDialogId);
        }

        return this.jobApi.getBillingPreview$(job.id).pipe(
          switchMap((billingPreview) => {
            if (!billingPreview) {
              this._snackBarService.pushErrorMessage();
              return EMPTY;
            }

            if (billingPreview.requiresStripeValidation && !billingPreview.stripeValidation.isValid) {
              return this.openJobFeesDialogForBilling$(job, jobFeesDialogId);
            }

            this.setConfirmBillingLoading(false);
            const confirmDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogComponentData, ConfirmDialogAction>(ConfirmDialogComponent, {
              id: confirmBillingDialogId,
              width: 'auto',
              data: {
                title: 'Confirm Billing',
                template: this.confirmBillingDialogTemplate,
                confirmBtnTxt: 'Yes',
                cancelBtnTxt: 'Cancel',
              },
            });

            return confirmDialogRef.afterClosed().pipe(
              switchMap(result => {
                if (result === ConfirmDialogAction.Confirm) {
                  return this.confirmBillingAfterFeesUpdate(job.id);
                }
                return EMPTY;
              })
            );
          }),
        );
      }),
      catchError(e => {
        this._snackBarService.pushErrorMessage();
        this.setConfirmBillingLoading(false);
        return EMPTY;
      }),
      finalize(() => this.setConfirmBillingLoading(false)),
      untilDestroyed(this)
    ).subscribe();
  }

  private setConfirmBillingLoading(isLoading: boolean): void {
    if (this.confirmBillingLoading === isLoading) {
      return;
    }

    this.confirmBillingLoading = isLoading;
    this.cdr.markForCheck();
  }

  private openJobFeesDialogForBilling$(job: Job, dialogId: string): Observable<unknown> {
    this.setConfirmBillingLoading(false);
    const dialogRef = this.dialog.open(JobFeesDialogComponent, {
      id: dialogId,
      width: '900px',
      data: {
        job,
      },
    });

    return dialogRef.afterClosed().pipe(
      switchMap((updatedOrderDetails: OrderDetail[]) => {
        if (updatedOrderDetails) {
          return this.confirmBillingAfterFeesUpdate(job.id);
        }

        return EMPTY;
      }),
    );
  }

  // Helper method to encapsulate the actual billing confirmation logic
  private confirmBillingAfterFeesUpdate(jobId: string): Observable<any> {
    return this.jobApi.confirmBilling$(jobId).pipe(
      this.screenSpinnerService.withSpinner(),
      tap(job => {
        if (job) {
          this._snackBarService.pushSuccessAlert('Billing confirmed!');
        } else {
          this._snackBarService.pushErrorMessage();
        }
      }),
      switchMap(job => job ? this._reloadCurrentJob(job.id) : EMPTY),
      catchError(e => {
        this._snackBarService.pushErrorMessage();
        return EMPTY;
      })
    );
  }

  makePayment(job: Job) {
    const checkoutJob = this.getPrimaryPaymentCheckoutJob(job);

    if (!checkoutJob?.order?.payment) {
      this._snackBarService.pushErrorMessage();
      return;
    }

    void this.router.navigate(this.appRoutes.jobDetail(checkoutJob.id), {
      queryParams: buildJobMakePaymentQueryParams(),
    });
  }

  makeExtraJobPayment(extraJob: ExtraJob): void {
    if (!extraJob?.id || !extraJob?.jobId) {
      this._snackBarService.pushErrorMessage();
      return;
    }

    void this.router.navigate(this.appRoutes.jobDetail(extraJob.jobId), {
      queryParams: buildExtraJobMakePaymentQueryParams(extraJob.id),
    });
  }

  private openJobCheckoutDialog(job: Job): void {
    this.blurActiveElement();

    this.dialog.open(CheckoutDialogComponent, {
      width: '90vw',
      maxHeight: '90vh',
      panelClass: 'checkout-dialog-panel',
      data: {
        job,
      }
    });
  }

  private openExtraJobCheckoutDialog(extraJob: ExtraJob): void {
    this.blurActiveElement();

    this.dialog.open(CheckoutDialogComponent, {
      width: '90vw',
      maxHeight: '90vh',
      panelClass: 'checkout-dialog-panel',
      data: {
        extraJob,
      }
    });
  }

  private blurActiveElement(): void {
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  approvePayment(payment: Payment) {
    const paymentApproval = this.dialog.open(PaymentApprovalDialogComponent, {
      autoFocus: false,
      width: '50%',
      data: {
        action: ApprovalEnum.Approve,
        payment,
      },
    });

    this.job$.pipe(
      first(),
      filter(job => !!job?.id),
      switchMap(job => paymentApproval.afterClosed().pipe(
        switchMap(result => {
          // The dialog component now handles the API call and snackbar messages.
          // 'result' will be true if the action (approve/decline) was successful, false if cancelled or failed.
          if (result) {
            return this._reloadCurrentJob(job.id);
          }
          return EMPTY;
        }),
        catchError(e => {
          this._snackBarService.pushErrorMessage();
          return EMPTY;
        })
      )),
      untilDestroyed(this)
    ).subscribe()
  }

  confirmProfile$(jobId: string) {
    return this.jobApi.confirmProfile$(jobId).pipe(
      this.screenSpinnerService.withSpinner(),
      tap(updatedJob => {
        if (updatedJob) {
          this._snackBarService.pushSuccessAlert('Profile confirmed!');
        } else {
          this._snackBarService.pushErrorMessage();
        }
      }),
      switchMap(updatedJob => updatedJob ? this._reloadCurrentJob(updatedJob.id) : EMPTY),
      catchError(e => {
        this._snackBarService.pushErrorMessage();
        return EMPTY;
      })
    )
  }
  confirmProfile(jobId: string) {
    const confirmDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogComponentData, ConfirmDialogAction>(ConfirmDialogComponent, {
      width: 'auto',
      data: {
        title: 'Confirmation',
        template: this.confirmProfileDialogTemplate,
        confirmBtnTxt: 'Yes',
        cancelBtnTxt: 'Cancel',
      },
    });

    confirmDialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result === ConfirmDialogAction.Confirm) {
          return this.confirmProfile$(jobId)
        }
        return EMPTY; // User cancelled
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  confirmDocumentsPrepared(jobId: string): void {
    const confirmDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogComponentData, ConfirmDialogAction>(ConfirmDialogComponent, {
      width: 'auto',
      data: {
        title: 'Confirmation',
        template: this.confirmDocumentsPreparedDialogTemplate,
        confirmBtnTxt: 'Yes',
        cancelBtnTxt: 'Cancel',
      },
    });

    confirmDialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result === ConfirmDialogAction.Confirm) {
          return this.jobApi.prepareDocument$(jobId).pipe(
            this.screenSpinnerService.withSpinner(),
            tap(job => {
              if (job) {
                this._snackBarService.pushSuccessAlert('Documents marked as prepared!');
                this.syncTabQueryParam(JobDetailTab.DocumentTemplates);
              } else {
                this._snackBarService.pushErrorMessage();
              }
            }),
            switchMap(job => job ? this._reloadCurrentJob(job.id) : EMPTY),
            catchError(e => {
              this._snackBarService.pushErrorMessage();
              return EMPTY;
            })
          );
        }
        return EMPTY; // User cancelled
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  confirmMarkAsComplete(jobId: string): void {
    const confirmDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogComponentData, ConfirmDialogAction>(ConfirmDialogComponent, {
      width: 'auto',
      data: {
        title: 'Confirmation',
        template: this.confirmMarkAsCompleteDialogTemplate,
        confirmBtnTxt: 'Yes',
        cancelBtnTxt: 'Cancel',
      },
    });

    confirmDialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result === ConfirmDialogAction.Confirm) {
          return this.jobApi.markAsComplete$(jobId).pipe(
            this.screenSpinnerService.withSpinner(),
            tap(job => {
              if (job) {
                this._snackBarService.pushSuccessAlert('Job marked as complete!');
              } else {
                this._snackBarService.pushErrorMessage();
              }
            }),
            switchMap(job => job ? this._reloadCurrentJob(job.id) : EMPTY),
            catchError(e => {
              this._snackBarService.pushErrorMessage();
              return EMPTY;
            })
          );
        }
        return EMPTY; // User cancelled
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private _reloadCurrentJob(jobId: string): Observable<Job> {
    return this.jobApi.find$(jobId).pipe(
      tap(entity => this.jobService.dispatchSetActiveEntityAction(entity)),
      tap(entity => this.clearProcessingForJob(entity)),
      filter(job => !!job),
      first()
    );
  }

  private loadExtraJobs$(jobId: string, force = false): Observable<ExtraJob[]> {
    if (!jobId) {
      return of([]);
    }

    if (!force && this.extraJobsLoadedForJobId === jobId) {
      return of(this.extraJobsState$.value);
    }

    return this.jobApi.findExtraJobs$(jobId).pipe(
      tap((extraJobs) => {
        this.extraJobsLoadedForJobId = jobId;
        this.setExtraJobsState(extraJobs);
        this.clearProcessingForExtraJobs(extraJobs);
      }),
      catchError((e) => {
        console.error(e);
        return of(this.extraJobsState$.value);
      }),
      first(),
    );
  }

  private refreshExtraJobs$(jobId: string): Observable<ExtraJob[]> {
    return this.loadExtraJobs$(jobId, true);
  }

  private setExtraJobsState(extraJobs: ExtraJob[]): void {
    this.extraJobsState$.next([...(extraJobs ?? [])]);
  }

  private resetExtraJobsState(): void {
    this.extraJobsLoadedForJobId = null;
    this.setExtraJobsState([]);
    this.syncExtraJobPaymentSubscriptions([]);
  }

  private clearProcessingForJob(job: Job): void {
    if (!job) {
      return;
    }

    const primaryPayment = job.parentId
      ? job.parent?.order?.payment
      : job.order?.payment;

    if (this.isPaymentProcessingCompleted(primaryPayment)) {
      this.paymentService.clearProcessing(primaryPayment.id);
      const primaryOrderId = job.parentId
        ? job.parent?.order?.id
        : job.order?.id;

      if (primaryOrderId) {
        this.paymentService.clearOrderProcessing(primaryOrderId);
        this.paymentService.clearOrderAwaitingRefresh(primaryOrderId);
      }
    }

    this.clearProcessingForExtraJobs(this.extraJobsState$.value);
  }

  private clearProcessingForExtraJobs(extraJobs: ExtraJob[]): void {
    (extraJobs ?? []).forEach(extraJob => {
      const payment = extraJob?.order?.payment;
      if (this.shouldClearPaymentLocks(payment)) {
        this.paymentService.clearProcessing(payment.id);
        if (extraJob.order?.id) {
          this.paymentService.clearOrderProcessing(extraJob.order.id);
          this.paymentService.clearOrderAwaitingRefresh(extraJob.order.id);
        }
      }
    });
  }

  private isPaymentProcessingCompleted(payment?: Payment | null): payment is Payment {
    if (!payment?.id) {
      return false;
    }

    return payment.isPaid ||
      payment.status === PaymentStatusEnum.Paid ||
      payment.status === PaymentStatusEnum.ReviewingPayment ||
      payment.status === PaymentStatusEnum.Closed;
  }

  private shouldClearPaymentLocks(payment?: Payment | null): payment is Payment {
    if (!payment?.id) {
      return false;
    }

    return payment.isPaid ||
      payment.status === PaymentStatusEnum.Paid ||
      payment.status === PaymentStatusEnum.ReviewingPayment ||
      payment.status === PaymentStatusEnum.Closed ||
      payment.isPaymentToDo ||
      payment.canMakePayment;
  }

  private handleMakePaymentRouteAction(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
      map(([routeParams, queryParams]) => ({
        jobId: routeParams.get('id') ?? '',
        action: queryParams.get(JOB_DETAIL_ACTION_QUERY_PARAM),
        extraJobId: queryParams.get(JOB_DETAIL_EXTRA_JOB_ID_QUERY_PARAM),
      })),
      distinctUntilChanged((prev, next) =>
        prev.jobId === next.jobId
        && prev.action === next.action
        && prev.extraJobId === next.extraJobId,
      ),
      filter(({jobId, action}) => Boolean(jobId) && action === JOB_DETAIL_MAKE_PAYMENT_ACTION),
      switchMap(({jobId, extraJobId}) => this.job$.pipe(
        filter((job): job is Job => job?.id === jobId),
        first(),
        tap(job => {
          void this.consumeMakePaymentRouteAction(job, extraJobId);
        })
      )),
      untilDestroyed(this)
    ).subscribe();
  }

  private async consumeMakePaymentRouteAction(job: Job, extraJobId: string | null): Promise<void> {
    this.clearMakePaymentRouteActionFromUrl();

    if (extraJobId) {
      const extraJob = await firstValueFrom(
        this.resolveExtraJobForCheckout$(job.id, extraJobId),
      );

      if (extraJob?.order?.payment?.canMakePayment) {
        this.openExtraJobCheckoutDialog(extraJob);
      }

      return;
    }

    const payment = this.getPrimaryPayment(job);

    if (payment?.canMakePayment) {
      const checkoutJob = this.getPrimaryPaymentCheckoutJob(job);

      if (checkoutJob) {
        this.openJobCheckoutDialog(checkoutJob);
      }
    }
  }

  private resolveExtraJobForCheckout$(jobId: string, extraJobId: string): Observable<ExtraJob | null> {
    const currentExtraJob = this.extraJobsState$.value.find((item) => item.id === extraJobId) ?? null;

    if (currentExtraJob) {
      return of(currentExtraJob);
    }

    return this.refreshExtraJobs$(jobId).pipe(
      map((extraJobs) => extraJobs.find((item) => item.id === extraJobId) ?? null),
    );
  }

  private getPrimaryPaymentCheckoutJob(job: Job): Job | null {
    if (!job) {
      return null;
    }

    if (!job.parentId) {
      return job;
    }

    return job.parent ?? null;
  }

  private getPrimaryPayment(job: Job): Payment | null {
    return this.getPrimaryPaymentCheckoutJob(job)?.order?.payment ?? null;
  }

  private resolveTab(tabParam: string | null): JobDetailTab | null {
    if (!tabParam || !Object.values(JobDetailTab).includes(tabParam as JobDetailTab)) {
      return null;
    }

    return tabParam as JobDetailTab;
  }

  private createTabActive$(tab: JobDetailTab): Observable<boolean> {
    return combineLatest([
      this.selectedIndex$,
      this.visibleTabs$,
    ]).pipe(
      map(([selectedIndex, visibleTabs]) => visibleTabs[selectedIndex] === tab),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private syncTabQueryParam(tab: JobDetailTab): void {
    if (this.getCurrentBrowserQueryParam(JOB_DETAIL_TAB_QUERY_PARAM) === tab) {
      return;
    }

    this.replaceBrowserQueryParams({
      [JOB_DETAIL_TAB_QUERY_PARAM]: tab,
    });
    this.tabSelectionSync$.next(undefined);
  }

  private clearMakePaymentRouteActionFromUrl(): void {
    this.replaceBrowserQueryParams({
      [JOB_DETAIL_ACTION_QUERY_PARAM]: null,
    });
  }

  private replaceBrowserQueryParams(queryParams: Record<string, string | null>): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
      fragment: this.route.snapshot.fragment ?? undefined,
    });
  }

  private getCurrentBrowserQueryParam(key: string): string | null {
    return this.route.snapshot.queryParamMap.get(key);
  }

  private subscribeToPaymentUpdates(): void {
    this.job$.pipe(
      filter(job => !!job?.id),
      tap(job => this.subscribeToPrimaryPaymentUpdates(job)),
      untilDestroyed(this)
    ).subscribe();

    combineLatest([this.job$, this.extraJobs$]).pipe(
      filter(([job]) => !!job?.id),
      tap(([job, extraJobs]) => {
        this.syncExtraJobPaymentSubscriptions(extraJobs, job.id);
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private subscribeToPrimaryPaymentUpdates(job: Job): void {
    const paymentId = job.parentId
      ? job.parent?.order?.payment?.id
      : job.order?.payment?.id;

    if (paymentId === this.primaryPaymentId) {
      return;
    }

    this.primaryPaymentId = paymentId ?? null;
    this.paymentStatusSubscription?.leaveChannel();
    this.paymentStatusSubscription = null;

    if (!paymentId) {
      return;
    }

    this.paymentStatusSubscription = this.pusherService.subscribe(
      `payments.${paymentId}`,
      AppEventEnum.PaymentStatusUpdated,
      payload => {
        const outcome = payload.outcome as string | undefined;
        const shouldClearProcessing = outcome === 'failed' || outcome === 'rejected';

        if (shouldClearProcessing) {
          this.paymentService.clearProcessing(payload.paymentId);
          if (payload.orderId) {
            this.paymentService.clearOrderProcessing(payload.orderId);
            this.paymentService.clearOrderAwaitingRefresh(payload.orderId);
          }
        }
        this._reloadCurrentJob(job.id).subscribe();
      }
    );
  }

  private syncExtraJobPaymentSubscriptions(extraJobs: ExtraJob[], jobId?: string): void {
    const paymentIds = (extraJobs ?? [])
      .map(extraJob => extraJob.order?.payment?.id)
      .filter((paymentId): paymentId is string => Boolean(paymentId));

    const nextIds = new Set(paymentIds);

    this.extraJobPaymentSubscriptions.forEach((subscription, paymentId) => {
      if (!nextIds.has(paymentId)) {
        subscription.leaveChannel();
        this.extraJobPaymentSubscriptions.delete(paymentId);
      }
    });

    nextIds.forEach(paymentId => {
      if (this.extraJobPaymentSubscriptions.has(paymentId)) {
        return;
      }

      const subscription = this.pusherService.subscribe(
        `payments.${paymentId}`,
        AppEventEnum.PaymentStatusUpdated,
        payload => {
          const outcome = payload.outcome as string | undefined;
          const shouldClearProcessing = outcome === 'failed' || outcome === 'rejected';

          if (shouldClearProcessing) {
            this.paymentService.clearProcessing(payload.paymentId);
            if (payload.orderId) {
              this.paymentService.clearOrderProcessing(payload.orderId);
              this.paymentService.clearOrderAwaitingRefresh(payload.orderId);
            }
          }

          if (jobId) {
            this.refreshExtraJobs$(jobId).subscribe();
          }
        }
      );

    this.extraJobPaymentSubscriptions.set(paymentId, subscription);
  });
  }

}
